import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Award, Plus, Loader2, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CertStatus = 'active' | 'expiring_soon' | 'expired' | 'pending';

interface Certification {
  id: string;
  name: string;
  issuer: string;
  framework: string;
  status: CertStatus;
  issued_date: string;
  expiry_date: string;
  renewal_progress: number;
  created_at: string;
}

interface CertificationsResponse {
  certifications: Certification[];
  stats: {
    total: number;
    active: number;
    expiring_soon: number;
    expired: number;
  };
}

interface CreateCertPayload {
  name: string;
  issuer: string;
  framework: string;
  status: CertStatus;
  issued_date: string;
  expiry_date: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadge(status: CertStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'active':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Active</span>;
    case 'expiring_soon':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Expiring Soon</span>;
    case 'expired':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Expired</span>;
    case 'pending':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Pending</span>;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function progressColor(pct: number): string {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function CertificationsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateCertPayload>({
    name: '',
    issuer: '',
    framework: '',
    status: 'pending',
    issued_date: '',
    expiry_date: '',
  });

  const certsQuery = useQuery<CertificationsResponse>({
    queryKey: ['governance', 'certifications'],
    queryFn: () => api<CertificationsResponse>('governance/certifications'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateCertPayload) =>
      api('governance/certifications', { body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'certifications'] });
      setShowForm(false);
      setForm({ name: '', issuer: '', framework: '', status: 'pending', issued_date: '', expiry_date: '' });
    },
  });

  const stats = certsQuery.data?.stats;
  const certs = certsQuery.data?.certifications ?? [];

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.issuer.trim()) return;
    createMutation.mutate(form);
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Award className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">AI Certifications</h1>
            <p className="text-muted-foreground mt-1">
              Track AI certifications, renewal dates, and compliance status.
            </p>
          </div>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
            Add Certification
          </button>
        )}
      </div>

      {/* Stats */}
      {certsQuery.isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Total Certifications</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-500">{stats?.active ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Expiring Soon</p>
            <p className="text-2xl font-bold text-yellow-500">{stats?.expiring_soon ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Expired</p>
            <p className="text-2xl font-bold text-red-500">{stats?.expired ?? 0}</p>
          </div>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Add Certification</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Certification Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. ISO 42001 AI Management System"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Issuer</label>
              <input
                required
                value={form.issuer}
                onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                placeholder="e.g. BSI, AICPA, ISO"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Framework</label>
              <input
                value={form.framework}
                onChange={(e) => setForm({ ...form, framework: e.target.value })}
                placeholder="e.g. ISO 42001, NIST AI RMF"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Issued Date</label>
              <input
                type="date"
                value={form.issued_date}
                onChange={(e) => setForm({ ...form, issued_date: e.target.value })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Expiry Date</label>
              <input
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {createMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to add certification.'}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={createMutation.isPending || !form.name.trim() || !form.issuer.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {createMutation.isPending ? 'Adding...' : 'Add Certification'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Certifications list */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">All Certifications</h2>

        {certsQuery.isLoading && (
          <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading certifications...</span>
          </div>
        )}

        {certsQuery.isSuccess && certs.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
            No certifications tracked yet. Add your first certification to get started.
          </div>
        )}

        {certsQuery.isSuccess && certs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certs.map((cert) => (
              <div key={cert.id} className="rounded-lg border border-border bg-card p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-medium text-foreground truncate">{cert.name}</h3>
                    <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                  </div>
                  {statusBadge(cert.status)}
                </div>
                {cert.framework && (
                  <p className="text-xs text-muted-foreground">Framework: {cert.framework}</p>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  {cert.issued_date && <span>Issued: {formatDate(cert.issued_date)}</span>}
                  {cert.expiry_date && <span>Expires: {formatDate(cert.expiry_date)}</span>}
                </div>
                {cert.renewal_progress !== undefined && cert.renewal_progress > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Renewal Progress</span>
                      <span className="text-xs font-medium text-foreground">{cert.renewal_progress}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${progressColor(cert.renewal_progress)} transition-all`}
                        style={{ width: `${cert.renewal_progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
